import { TaskStatus } from '@/services/enums';
/**
 * K线任务服务（提示词分析、监控规则分析等）
 */

import { apiClient } from './apiClient';
import type { MonitorRulesResultDto } from './klineTypes';

export class KlineTaskService {
  /**
   * AI分析自定义提示词（异步任务，立即返回 taskId）
   * @param promptHash prompt 的 hash 值
   * @param metadata 元数据：symbol, bar, startTime, endTime
   */
  async analyzePrompt(
    promptHash: string,
    metadata?: {
      symbol?: string;
      bar?: string;
      startTime?: number;
      endTime?: number;
    },
  ): Promise<{ taskId: string }> {
    const response = await apiClient.post<{ taskId: string }>(
      '/kline/analyze-prompt',
      {
        promptHash,
        symbol: metadata?.symbol,
        bar: metadata?.bar,
        startTime: metadata?.startTime,
        endTime: metadata?.endTime,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.success) {
      throw new Error(response.message || '提交AI分析提示词任务失败');
    }

    return response.data;
  }

  /**
   * 获取提示词分析任务状态
   */
  async getPromptAnalysisStatus(taskId: string): Promise<{
    taskId: string;
    status: TaskStatus;
    result?: string;
    error?: string;
    monitorRules?: MonitorRulesResultDto;
  }> {
    const response = await apiClient.get<{
      taskId: string;
      status: TaskStatus;
      result?: string;
      error?: string;
      monitorRules?: MonitorRulesResultDto;
    }>('/kline/prompt/status', {
      params: { taskId },
      timeout: 10000, // 状态查询接口设置10秒超时
    });

    if (!response.success) {
      throw new Error(response.message || '获取任务状态失败');
    }

    return response.data;
  }

  /**
   * 根据symbol和bar查找正在运行的分析任务（提示词分析）
   */
  async findRunningTasks(
    symbol: string,
    bar: string,
  ): Promise<{
    promptTask: {
      taskId: string;
      status: TaskStatus;
      createdAt: string;
      startedAt?: string;
      finishedAt?: string;
      error?: string;
      result?: string;
      symbol: string;
      bar: string;
      startTime?: number;
      endTime?: number;
      promptHash: string;
    } | null;
  }> {
    const response = await apiClient.get<{
      promptTask: {
        taskId: string;
        status: TaskStatus;
        createdAt: string;
        startedAt?: string;
        finishedAt?: string;
        error?: string;
        result?: string;
        symbol: string;
        bar: string;
        startTime?: number;
        endTime?: number;
        promptHash: string;
      } | null;
    }>('/kline/tasks/find', {
      params: { symbol, bar },
    });

    if (!response.success) {
      throw new Error(response.message || '查找正在运行的任务失败');
    }

    return response.data;
  }
}

export const klineTaskService = new KlineTaskService();
